import React, { useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { lifeStyleFeatures, walkIndexFeatures } from "../../constants";
import ProgressBar from './Bar';
import './index.css';
import { StoreContext } from '../../store.js';
import Show from '../../utility/Show.jsx';
import {
  getLimit,
  getLifeStyleWording,
  getWalkIndexWording,
  getTypeDescription,
  getOverallFeelScore,
  lifeStyleFeaturesScores,
  isFeelScoreEmpty,
  getSoloFeelScore,
  hexToRgb,
  getArea,
} from '../../services';
import { WalkingDistanceButtons } from '../WalkingDistanceButtons/WalkingDistanceButtons.jsx';
import { ReactComponent as Back } from '../../assets/landing-page/back-button.svg';
import Skeleton from '../Skeleton/index.jsx';
import { Autocomplete } from '../../utility/Autocomplete.jsx';
import { ReactComponent as SearchIcon } from '../../assets/search.svg';
import NSVersion3 from '../NeighborhoodScore/version3.jsx';
import { WalkingLoader } from '../Loaders/index.jsx';
import useGoogleMaps from '../../useGoogleMaps.js';
import { setLSData } from "../../services";

const DiscoverNeighborhood = ({ overallScore, openEstablishments, setShowMap, handleBack }) => {
  const { state, dispatch, actions } = useContext(StoreContext);
  const {
    currentAddress,
    lifeStyleScores,
    walkindexScores,
    currentDistance,
    isStandalone,
    isMobile,
    showCompareLifeStyle,
    isModern,
    isLoading,
    isEmbedMode,
    clientName,
    currentTab,
    apiKey,
    isGooglePlacesLoaded,
    isGoogleMapsLoaded,
    isExploreEnabled,
  } = state;

  const isGoogleMapsReady = useGoogleMaps();
  const mapRef = useRef(null);
  const serviceRef = useRef();

  const isWalkIndex = currentTab === 'walkindex';
  const currentFeelScores = walkindexScores[0] || {};
  const isGemsEmpty = !lifeStyleScores || Object.keys(lifeStyleScores[0] || {}).length === 0;

  // Memoized filtered features
  const filteredLifeStyleFeatures = useMemo(
    () => lifeStyleFeatures.filter((feature) => feature.id !== 'All' && feature.id !== 'Favorites'),
    []
  );

  // Memoized style objects
  const bgColor = useMemo(() => {
    const branding = actions.getBrandingColor();
    return { '--color': branding["--color"] };
  }, [actions]);

  const textColor = useMemo(() => {
    const color = bgColor['--color'];
    return { '--text-color': color === '#fff' ? '#000' : '#fff' };
  }, [bgColor]);

  // Memoized scores
  const lifeStyleEssentialScores = useCallback(
    () => lifeStyleFeaturesScores(lifeStyleScores, 0, currentDistance, isStandalone),
    [lifeStyleScores, currentDistance, isStandalone]
  );

  const getWalkIndexOverallScore = useCallback(
    () => getOverallFeelScore(currentFeelScores),
    [currentFeelScores]
  );

  // Effects
  useEffect(() => {
    if (isGoogleMapsReady) {
      dispatch({
        type: 'SET_IS_GOOGLE_MAPS_LOADED',
        payload: isGoogleMapsReady,
      });
    }
    if (clientName) {
      setLSData('suffix', clientName);
    }
  }, [isGoogleMapsReady, clientName, dispatch]);

  // Combine fetchGems effect
  useEffect(() => {
    if (currentAddress && isGoogleMapsLoaded && (isGemsEmpty || currentDistance)) {
      const fetchGems = async () => {
        const gems = await actions.fetchLifestyleGems(
          currentAddress,
          0,
          'all',
          getArea(currentDistance),
        );
        if (gems?.length === 0) {
          const mapCenter = { lat: currentAddress?.latitude, lng: currentAddress?.longitude };
          await actions.fetchGooglePlacesMarkers(serviceRef, mapCenter, 0);
        }
      };
      fetchGems();
    }
    // eslint-disable-next-line
  }, [currentAddress, currentDistance, isGoogleMapsLoaded]);

  // Memoized handlers
  const getGemsOverallScore = useCallback(() => {
    const scores = Object.values(lifeStyleEssentialScores());
    const counts = [0, 0, 0];
    scores.forEach((score) => {
      const roundedScore = Math.round(score);
      counts[roundedScore - 1]++;
    });
    if (counts[2] >= 1) return 3;
    if (counts[1] >= 1) return 2;
    return 1;
  }, [lifeStyleEssentialScores]);

  const getFeelScore = useCallback((id) => getSoloFeelScore(currentFeelScores[id]), [currentFeelScores]);
  const getFeelItemStatus = useCallback((id) => (getFeelScore(id) >= 3 ? '' : 'inactive-feature'), [getFeelScore]);
  const getFeelDescription = useCallback(
    (feature, score) => getTypeDescription('walkindex', feature?.toLowerCase(), score, currentDistance, isStandalone, false),
    [currentDistance, isStandalone]
  );

  const redirectToMap = useCallback(() => {
    if (setShowMap) setShowMap(true);

    dispatch({ type: 'SET_PREVIOUS_PAGE', payload: 1 });
    if ((isMobile || isEmbedMode) && !isExploreEnabled) actions.toggleFullScreenMap();
    dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
    if (isWalkIndex) {
      dispatch({ type: 'SET_CURRENT_TAB', payload: 'lifestyle' });
    }
    if (clientName) {
      actions.toggleFullScreenMap();
    }
  }, [dispatch, actions, isMobile, isEmbedMode, isWalkIndex, clientName]);

  const redirectToFeelFeature = useCallback(
    async (feature, isWalkIndex) => {
      redirectToMap();
      if (isWalkIndex) {
        dispatch({ type: 'SET_CURRENT_TAB', payload: 'walkindex' });
      } else if (currentTab !== 'lifestyle') {
        dispatch({ type: 'SET_CURRENT_TAB', payload: 'lifestyle' });
      }
      dispatch({ type: 'SET_MAP_TAB', payload: feature });
      const tab = (isWalkIndex ? 'character' : 'gem') + 'category';
      await actions.handleUsageTracker(tab, feature, currentAddress);
    },
    [redirectToMap, dispatch, actions, currentTab]
  );

  const toggleCompareVisibility = useCallback(() => {
    if (setShowMap) setShowMap(true);
    dispatch({ type: 'SET_PREVIOUS_PAGE', payload: 1 });
    if (isMobile || isEmbedMode) actions.toggleFullScreenMap();
    dispatch({ type: 'SET_COMPARE_LIFESTYLE', payload: !showCompareLifeStyle });
    dispatch({ type: 'SET_CURRENT_TAB', payload: 'lifestyle' });
    dispatch({ type: 'SET_DESKTOP_SIDEBAR', payload: true });
    dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
    if (isModern) dispatch({ type: 'SET_SHOW_COMBINE_SEARCH', payload: true });
    dispatch({ type: 'SET_MAP_TAB', payload: 'All' });
  }, [dispatch, actions, isMobile, isEmbedMode, showCompareLifeStyle, isModern]);

  const getMinutes = useCallback(() => {
    switch (currentDistance) {
      case '1': return 5;
      case '2': return 10;
      case '3': return 20;
      default: return;
    }
  }, [currentDistance]);

  const gotoSearchAddress = useCallback(() => {
    // const referrer = document.referrer;

    // if (referrer.includes('walkspan.ai')) {
    //   window.history.back();
    // } else {
    //   dispatch({ type: 'SET_SHOW_SEARCH_ADDRESS', payload: true });
    // }
    dispatch({ type: 'SET_SHOW_SEARCH_ADDRESS', payload: true });
    // window.history.back();
  }, [dispatch]);

  const backFunction = useCallback(() => {
    if (isExploreEnabled) {
      handleBack();
    } else {
      gotoSearchAddress();
    }
  }, [isExploreEnabled]);

  const onLoad = useCallback(function callback(map1) {
    map1.setMapTypeId('satellite');
    dispatch({ type: 'SET_CURRENT_MAPTYPE', payload: 'satellite' });
    if (!showCompareLifeStyle) {
      dispatch({ type: 'SET_MAP_INSTANCE', payload: map1 });
    } else {
      dispatch({ type: 'SET_COMPARE_MAPS', payload: { index: 0, object: map1 } });
    }
    serviceRef.current = new window.google.maps.places.PlacesService(map1);
  }, [dispatch, showCompareLifeStyle]);

  // Render gem scores
  const renderGemScores = useCallback(() => {
    if (!lifeStyleScores) return null;
    return <ProgressBar items={filteredLifeStyleFeatures} scores={lifeStyleScores?.[0]} itemClick={redirectToFeelFeature} state={state} />;
  }, [lifeStyleScores, filteredLifeStyleFeatures, redirectToFeelFeature]);

  return (
    <div
      className="discover-neighborhood relative"
      style={{
        ...bgColor,
        ...textColor
      }}
    >
      {/* <Show.When isTrue={isGoogleMapsLoaded}>
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%', display: 'none' }}
          center={{
            lat: parseFloat(currentAddress?.latitude),
            lng: parseFloat(currentAddress?.longitude),
          }}
          onLoad={onLoad}
        />
      </Show.When> */}
      <div className="discover-header-container flex-col md:flex-row">
        <div className='flex items-start gap-x-2'>
          <Show.When isTrue={!clientName}>
            <Back
              className='discover-back mt-1'
              onClick={backFunction}
            />
          </Show.When>
          <div className='flex flex-col gap-y-1'>
            <h1 className='text-lg md:text-2xl font-bold tracking-wider'>DISCOVER THE NEIGHBORHOOD</h1>
            <Autocomplete
              className='summary-autocomplete w-full md:w-[90%]'
              placeholder='Type an address here'
              value={currentAddress?.property}
              icon={SearchIcon}
            />
            <span>{getMinutes()} min walking radius</span>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center my-4 md:my-0 mx-auto md:mx-0">
          <span className="text-lg">Walking Distance</span>
          <WalkingDistanceButtons isTitle={false} showTitle={false} key="dashboard-walking-distance-btn" />
        </div>
      </div>

      {/* GEMS SCORE */}
      <div className="summary-parent-container mt-8 md:mt-0">
        <Show>
          <Show.When isTrue={!isLoading}>
            <div className="gems-score-container flex-col md:flex-row">
              <div className="gem-score-summary w-full md:w-[275px]">
                <h2 className="absolute w-full -top-10 left-1/2 transform -translate-x-1/2 text-center md:text-left text-lg tracking-wider font-medium">
                  LIFESTYLE ESSENTIALS
                </h2>
                <NSVersion3 isWalkIndex={false} score={getGemsOverallScore()} width="55px" fontSize="18px"/>
              </div>
              <div className='w-full'>
                {renderGemScores()}
              </div>
            </div>
          </Show.When>
          <Show.Else>
            <div className="gems-score-container">
              <Skeleton count={1} size={55} />
            </div>
          </Show.Else>
        </Show>
      </div>

      {/* Character Score */}
      <Show.When isTrue={!isStandalone}>
        <Show.When isTrue={isLoading}>
          <div className="feel-score-container">
            <Skeleton count={1} size={55} />
          </div>
        </Show.When>
        <Show.When isTrue={!isLoading}>
          <div className="summary-parent-container mt-8">
            <div className="feel-score-container flex-col md:flex-row">
              <div className="gem-score-summary w-full md:w-[275px]">
                <h2 className="absolute w-full -top-10 left-1/2 transform -translate-x-1/2 text-center md:text-left text-lg tracking-wider">
                  SIDEWALK CHARACTER
                </h2>
                <NSVersion3 isWalkIndex={true} score={getWalkIndexOverallScore()} width="55px" fontSize="18px"/>
                <span className="score-description"></span>
              </div>
              <div className="feel-details w-full flex-col md:flex-row">
                {walkIndexFeatures.map((feature) => (
                  <div
                    key={feature.id}
                    className={`feel-feature ${getFeelItemStatus(feature.id)}`}
                    style={{ '--icon-color': hexToRgb(feature.color) }}
                    onClick={()=> redirectToFeelFeature(feature.name, true)}
                  >
                    {feature.svg}
                    <h3>{feature.name}</h3>
                    <span>{getFeelDescription(feature.name, getFeelScore(feature.id))}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Show.When>
      </Show.When>

      {/* ACTION BUTTONS */}
      <div className="action-buttons flex-col md:flex-row">
        <button className="view-maps flex-[0_0_0] md:flex-[0_0_200px] max-w-[100%] md:max-w-[200px]" onClick={redirectToMap}>VIEW MAPS</button>
        <button className="quick-search flex-[0_0_0] md:flex-[0_0_200px] max-w-[100%] md:max-w-[200px]" onClick={openEstablishments}>MULTIPLE SEARCH</button>
        <button className="compare-addresses flex-[0_0_0] md:flex-[0_0_200px] max-w-[100%] md:max-w-[200px]" onClick={toggleCompareVisibility}>COMPARE ADDRESSES</button>
      </div>
      <Show.When isTrue={!isGooglePlacesLoaded}>
        <WalkingLoader blurValue='2.5px' />
      </Show.When>
    </div>
  );
};

export default DiscoverNeighborhood;
